/**
 * ClientPortalAnalyses - Dossier de presentation du projet (analyses PDF envoyees au client).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

const ClientPortalAnalyses = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/my-analyses`);
      setDocs(Array.isArray(r.data) ? r.data : []);
    } catch {
      setDocs([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const download = async (doc) => {
    setDownloadingId(doc.doc_id);
    try {
      const resp = await axios.get(`${API}/portal/analyses/${doc.doc_id}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || `analyse_${doc.doc_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      alert('Erreur lors du telechargement.');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="realease-card p-6" data-testid="client-analyses-loading">
        <p className="text-sm text-gray-400 italic">Chargement de votre dossier...</p>
      </div>
    );
  }

  return (
    <div className="realease-card p-6" data-testid="client-analyses-section">
      <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2 text-lg">
        <svg className="w-5 h-5 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Dossier de presentation
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        Analyses detaillees, etudes de rentabilite et documents de presentation prepares par RealEase.
      </p>

      {docs.length === 0 ? (
        <div className="text-center py-8" data-testid="client-analyses-empty">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm text-gray-400 italic">
            Aucun document de presentation pour le moment.<br />
            Votre conseiller vous enverra prochainement les analyses de votre projet.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="client-analyses-list">
          {docs.map((doc) => (
            <div
              key={doc.doc_id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid={`client-analysis-${doc.doc_id}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-md bg-[#13736D]/10 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">{doc.filename}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {doc.label && (
                      <span className="text-[10px] uppercase tracking-wider text-[#A57F4F] font-bold">
                        {doc.label}
                      </span>
                    )}
                    {doc.created_at && (
                      <span className="text-xs text-gray-500">{formatDate(doc.created_at)}</span>
                    )}
                    {doc.project_name && (
                      <span className="text-xs text-gray-400 truncate">{doc.project_name}</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => download(doc)}
                disabled={downloadingId === doc.doc_id}
                className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition shrink-0"
                data-testid={`client-analysis-download-${doc.doc_id}`}
              >
                {downloadingId === doc.doc_id ? '...' : 'Telecharger'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientPortalAnalyses;
